import { Link } from 'react-router-dom';
import Header from '../components/Header';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-gray-600">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                Welcome to ProductTrace. These Terms of Service ("Terms") govern your use of the 
                ProductTrace platform and services (the "Service") operated by ProductTrace ("we," "us," or "our").
              </p>
              <p className="text-gray-700">
                By accessing or using our Service, you agree to be bound by these Terms. If you 
                disagree with any part of these terms, then you may not access the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                ProductTrace is a product analysis platform that provides:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>AI-powered product component analysis</li>
                <li>Manufacturing process insights</li>
                <li>Environmental impact assessments</li>
                <li>Labor condition information</li>
                <li>Geographic origin tracking</li>
                <li>Supply chain visualization</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">3.1 Account Creation</h3>
              <p className="text-gray-700 mb-4">
                To access certain features of our Service, you must create an account using Google OAuth. 
                You agree to provide accurate, current, and complete information during the registration process.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">3.2 Account Security</h3>
              <p className="text-gray-700 mb-4">
                You are responsible for safeguarding your account and all activities that occur under your account. 
                You agree to immediately notify us of any unauthorized use of your account.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">3.3 Account Termination</h3>
              <p className="text-gray-700">
                We reserve the right to suspend or terminate your account at any time for violations 
                of these Terms or for any other reason at our sole discretion.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Acceptable Use</h2>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">4.1 Permitted Use</h3>
              <p className="text-gray-700 mb-4">You may use our Service for:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Legitimate product research and analysis</li>
                <li>Educational and informational purposes</li>
                <li>Business intelligence and decision-making</li>
                <li>Supply chain transparency initiatives</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">4.2 Prohibited Use</h3>
              <p className="text-gray-700 mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Use the Service for any unlawful purpose or in violation of any laws</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Interfere with or disrupt the Service or servers connected to the Service</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                <li>Upload or transmit viruses, malware, or other malicious code</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Content and Data</h2>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">5.1 Our Content</h3>
              <p className="text-gray-700 mb-4">
                The Service and its original content, features, and functionality are owned by 
                ProductTrace and are protected by international copyright, trademark, patent, 
                trade secret, and other intellectual property laws.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">5.2 User-Generated Content</h3>
              <p className="text-gray-700 mb-4">
                You retain ownership of any content you submit to the Service. However, by submitting 
                content, you grant us a worldwide, non-exclusive, royalty-free license to use, 
                reproduce, modify, and distribute such content in connection with the Service.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">5.3 Data Accuracy</h3>
              <p className="text-gray-700">
                While we strive to provide accurate information, the data and analysis provided 
                by our Service are for informational purposes only. We do not guarantee the 
                accuracy, completeness, or reliability of any information provided.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Privacy</h2>
              <p className="text-gray-700">
                Your privacy is important to us. Please review our Privacy Policy, which also 
                governs your use of the Service, to understand our practices regarding the 
                collection and use of your information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Third-Party Services</h2>
              <p className="text-gray-700 mb-4">
                Our Service may integrate with third-party services, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Google OAuth for authentication</li>
                <li>Mapbox for geographic visualizations</li>
                <li>OpenRouter for AI-powered analysis</li>
                <li>Other analytics and monitoring services</li>
              </ul>
              <p className="text-gray-700">
                These third-party services have their own terms of service and privacy policies. 
                We are not responsible for the content, privacy policies, or practices of any 
                third-party services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Disclaimers</h2>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">8.1 Service Availability</h3>
              <p className="text-gray-700 mb-4">
                We strive to maintain the availability of our Service, but we do not guarantee 
                that the Service will be available at all times. The Service may be subject to 
                planned or unplanned downtime.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">8.2 Information Accuracy</h3>
              <p className="text-gray-700 mb-4">
                The information provided by our Service is generated using AI and various data sources. 
                While we strive for accuracy, we cannot guarantee that all information is complete, 
                accurate, or up-to-date.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">8.3 "As Is" Basis</h3>
              <p className="text-gray-700">
                The Service is provided on an "as is" and "as available" basis without any 
                warranties of any kind, either express or implied, including but not limited 
                to warranties of merchantability, fitness for a particular purpose, or non-infringement.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the fullest extent permitted by applicable law, ProductTrace shall not be liable for:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Any loss of profits, revenues, data, or use</li>
                <li>Any damages resulting from your use or inability to use the Service</li>
                <li>Any damages resulting from reliance on information provided by the Service</li>
                <li>Any damages exceeding the amount paid by you for the Service in the past 12 months</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Indemnification</h2>
              <p className="text-gray-700">
                You agree to defend, indemnify, and hold harmless ProductTrace and its officers, 
                directors, employees, and agents from and against any claims, damages, obligations, 
                losses, liabilities, costs, or debt arising from your use of the Service or violation 
                of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Termination</h2>
              <p className="text-gray-700 mb-4">
                We may terminate or suspend your account and access to the Service immediately, 
                without prior notice or liability, for any reason, including if you breach these Terms.
              </p>
              <p className="text-gray-700">
                Upon termination, your right to use the Service will cease immediately. All provisions 
                of these Terms that by their nature should survive termination shall survive, including 
                ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Governing Law</h2>
              <p className="text-gray-700">
                These Terms shall be governed by and construed in accordance with the laws of 
                [Your Jurisdiction], without regard to its conflict of law provisions. Any disputes 
                arising under these Terms shall be subject to the exclusive jurisdiction of the 
                courts of [Your Jurisdiction].
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Changes to Terms</h2>
              <p className="text-gray-700">
                We reserve the right to modify or replace these Terms at any time. If a revision 
                is material, we will provide at least 30 days notice prior to any new terms taking 
                effect. Your continued use of the Service after such changes constitutes acceptance 
                of the new Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Severability</h2>
              <p className="text-gray-700">
                If any provision of these Terms is held to be invalid or unenforceable, the remaining 
                provisions will remain in full force and effect. The invalid or unenforceable provision 
                will be replaced with a valid provision that most closely matches the intent of the 
                original provision.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Entire Agreement</h2>
              <p className="text-gray-700">
                These Terms, together with our Privacy Policy, constitute the entire agreement 
                between you and ProductTrace regarding the use of the Service and supersede all 
                prior and contemporaneous agreements and understandings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> legal@producttrace.io<br />
                </p>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <Link 
                to="/privacy" 
                className="text-gray-600 hover:text-gray-800 mb-4 sm:mb-0"
              >
                ← View Privacy Policy
              </Link>
              <Link 
                to="/" 
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Back to ProductTrace →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService; 